/* <i80-pdf>: an i80 component of our own (not in Helios), built only from i80 tokens.
   A calm PDF reader you can select text in. The page sits on a calm canvas with rounded
   corners; one accent colour appears only where it means something — the selection and the
   bound (marked) passages. The text layer is pdf.js' own, laid over the rendered canvas so
   real text can be selected; the rules it needs are included below, scoped to the component. */
.i80-pdf {
  --i80-pdf-accent-tint: var(--i80-color-palette-red-100);
  --i80-pdf-accent-ink: var(--i80-color-palette-red-500);
  --i80-pdf-accent-edge: var(--i80-color-palette-red-300);
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: var(--i80-color-foreground-primary);
  font-family: var(--i80-typography-font-stack-text);
}

/* --- toolbar --- */
.i80-pdf__toolbar {
  display: flex;
  gap: 6px;
  align-items: center;
}

.i80-pdf__spacer { flex: 1; }

.i80-pdf__pageinfo {
  min-width: 56px;
  padding: 0 6px;
  color: var(--i80-color-foreground-faint);
  font-size: var(--i80-typography-body-100-font-size);
  font-variant-numeric: tabular-nums;
  text-align: center;
}

.i80-pdf__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  color: var(--i80-color-foreground-strong);
  background: var(--i80-color-surface-primary);
  border: 1px solid var(--i80-color-border-primary);
  border-radius: var(--i80-border-radius-medium);
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease;
}
.i80-pdf__btn .i80-icon { width: 16px; height: 16px; }
.i80-pdf__btn:hover:not(:disabled) { background: var(--i80-color-surface-faint); border-color: var(--i80-color-border-strong); }
.i80-pdf__btn:disabled { color: var(--i80-color-foreground-disabled); cursor: default; opacity: 0.6; }
.i80-pdf__btn:focus-visible { outline: 3px solid var(--i80-color-focus-action-external); outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) { .i80-pdf__btn { transition: none; } }

/* --- the scrolling viewport --- */
.i80-pdf__viewport {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  overflow: auto;
  padding: 16px;
  background-color: var(--i80-color-surface-faint);
  border: 1px solid var(--i80-color-border-primary);
  border-radius: var(--i80-border-radius-large);
  box-shadow: var(--i80-elevation-inset-box-shadow);
}
.i80-pdf__viewport:focus-visible { outline: 3px solid var(--i80-color-focus-action-external); outline-offset: -3px; }

.i80-pdf__status {
  margin: auto;
  padding: 24px;
  color: var(--i80-color-foreground-faint);
  font-size: var(--i80-typography-body-200-font-size);
  text-align: center;
}

/* --- a page: canvas + text layer + marks, stacked --- */
.i80-pdf__page {
  position: relative;
  flex: 0 0 auto;
  background: #fff; /* a PDF page is white; the surface around it carries the theme */
  border-radius: var(--i80-border-radius-small);
  box-shadow: var(--i80-elevation-low-box-shadow);
}
.i80-pdf__canvas {
  display: block;
  border-radius: var(--i80-border-radius-small);
}

.i80-pdf ::selection { background: var(--i80-pdf-accent-tint); }

/* --- bound passages (read-only), drawn over the text --- */
.i80-pdf__marks {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
}
.i80-pdf__mark {
  position: absolute;
  margin: 0;
  padding: 0;
  background: var(--i80-color-palette-neutral-200);
  mix-blend-mode: multiply;
  border: 0;
  border-radius: 2px;
  cursor: pointer;
  pointer-events: auto;
  transition: background 120ms ease;
}
.i80-pdf__mark:hover { background: var(--i80-color-palette-neutral-300); }
.i80-pdf__mark--accent { background: var(--i80-pdf-accent-tint); box-shadow: inset 0 -2px 0 var(--i80-pdf-accent-edge); }
.i80-pdf__mark--accent:hover { background: var(--i80-pdf-accent-tint); box-shadow: inset 0 -2px 0 var(--i80-pdf-accent-ink); }
.i80-pdf__mark:focus-visible { outline: 2px solid var(--i80-color-focus-action-external); outline-offset: 1px; }
@media (prefers-reduced-motion: reduce) { .i80-pdf__mark { transition: none; } }

/* --- pdf.js text layer (scoped copy of its stylesheet, so no pdf_viewer.css is needed) --- */
.i80-pdf .textLayer {
  position: absolute;
  inset: 0;
  z-index: 1;
  text-align: initial;
  overflow: clip;
  opacity: 1;
  line-height: 1;
  text-size-adjust: none;
  forced-color-adjust: none;
  transform-origin: 0 0;
  caret-color: CanvasText;
  --min-font-size: 1;
  --text-scale-factor: calc(var(--total-scale-factor) * var(--min-font-size));
  --min-font-size-inv: calc(1 / var(--min-font-size));
}
.i80-pdf .textLayer :is(span, br) {
  color: transparent;
  position: absolute;
  white-space: pre;
  cursor: text;
  transform-origin: 0% 0%;
  user-select: text;
}
.i80-pdf .textLayer > :not(.markedContent),
.i80-pdf .textLayer .markedContent span:not(.markedContent) {
  z-index: 1;
  --font-height: 0;
  font-size: calc(var(--text-scale-factor) * var(--font-height));
  --scale-x: 1;
  --rotate: 0deg;
  transform: rotate(var(--rotate)) scaleX(var(--scale-x)) scale(var(--min-font-size-inv));
}
.i80-pdf .textLayer .markedContent { display: contents; }
